import type { Chain } from 'viem';
import {
  arbitrum,
  base,
  mainnet,
  optimism,
  polygon,
  sepolia,
} from 'wagmi/chains';

import type { ChainId } from '../types/api';

// ── Network type ────────────────────────────────────────────────────────────

export type NetworkType = 'evm' | 'hedera';

/**
 * Extended `ChainId` that includes the Hedera variant (not yet in the OpenAPI
 * spec). When the backend adds Hedera to the schema and types are regenerated,
 * this alias can be removed and all usages collapse back to `ChainId`.
 */
export type AppChainId = ChainId | { Hedera: string };

// ── Chain metadata (discriminated union) ────────────────────────────────────

interface EvmChainMeta {
  type: 'evm';
  chainNumber: number;
  label: string;
  explorerAddressUrl: string;
  wagmiChain: Chain;
}

interface HederaChainMeta {
  type: 'hedera';
  networkId: string;
  label: string;
  explorerAddressUrl: string;
}

export type ChainMeta = EvmChainMeta | HederaChainMeta;

// ── Chain registry (single source of truth) ─────────────────────────────────
//
// To add a new EVM chain:   add one entry here. Everything else derives from it.
// To add Hedera:            uncomment the Hedera entries once the backend ships the variant.

const ALL_CHAINS: readonly ChainMeta[] = [
  // EVM
  { type: 'evm', chainNumber: 1,        label: 'Ethereum Mainnet', explorerAddressUrl: 'https://etherscan.io/address/',            wagmiChain: mainnet },
  { type: 'evm', chainNumber: 11155111,  label: 'Sepolia Testnet',  explorerAddressUrl: 'https://sepolia.etherscan.io/address/',    wagmiChain: sepolia },
  { type: 'evm', chainNumber: 137,       label: 'Polygon',          explorerAddressUrl: 'https://polygonscan.com/address/',         wagmiChain: polygon },
  { type: 'evm', chainNumber: 42161,     label: 'Arbitrum One',     explorerAddressUrl: 'https://arbiscan.io/address/',             wagmiChain: arbitrum },
  { type: 'evm', chainNumber: 10,        label: 'Optimism',         explorerAddressUrl: 'https://optimistic.etherscan.io/address/', wagmiChain: optimism },
  { type: 'evm', chainNumber: 8453,      label: 'Base',             explorerAddressUrl: 'https://basescan.org/address/',            wagmiChain: base },
  // Hedera (uncomment when backend adds Hedera variant to ChainId)
  // { type: 'hedera', networkId: 'mainnet', label: 'Hedera Mainnet', explorerAddressUrl: 'https://hashscan.io/mainnet/account/' },
  // { type: 'hedera', networkId: 'testnet', label: 'Hedera Testnet', explorerAddressUrl: 'https://hashscan.io/testnet/account/' },
];

function metaToCanonicalKey(meta: ChainMeta): string {
  switch (meta.type) {
    case 'evm':    return `evm:${meta.chainNumber}`;
    case 'hedera': return `hedera:${meta.networkId}`;
  }
}

const registryByKey = new Map<string, ChainMeta>(
  ALL_CHAINS.map((c) => [metaToCanonicalKey(c), c]),
);

// ── Derived constants ───────────────────────────────────────────────────────

export const DEFAULT_CHAIN_ID: ChainId = { EVM: 11155111 };

export const SUPPORTED_CHAINS: readonly { chainId: AppChainId; label: string }[] =
  ALL_CHAINS.map((c) => ({
    chainId: c.type === 'evm' ? { EVM: c.chainNumber } : { Hedera: (c as HederaChainMeta).networkId },
    label: c.label,
  }));

export const WAGMI_CHAINS: readonly [Chain, ...Chain[]] = ALL_CHAINS
  .filter((c): c is EvmChainMeta => c.type === 'evm')
  .map((c) => c.wagmiChain) as unknown as [Chain, ...Chain[]];

export const ADDRESS_PATTERNS: Record<NetworkType, RegExp> = {
  evm: /^0x[a-fA-F0-9]{40}$/,
  hedera: /^0\.0\.\d+$/,
};

// ── Constructors ────────────────────────────────────────────────────────────

export function createEvmChainId(chainNumber: number): ChainId {
  return { EVM: chainNumber };
}

// ── Extractors ──────────────────────────────────────────────────────────────

export function evmChainNumber(chainId: AppChainId | undefined | null): number | null {
  if (chainId == null) return null;
  if ('EVM' in chainId && typeof chainId.EVM === 'number') return chainId.EVM;
  return null;
}

export function hederaNetworkId(chainId: AppChainId | undefined | null): string | null {
  if (chainId == null) return null;
  if ('Hedera' in chainId) {
    const raw = (chainId as { Hedera: string }).Hedera;
    return typeof raw === 'string' && raw.length > 0 ? raw : null;
  }
  return null;
}

export function networkType(chainId: AppChainId): NetworkType {
  if ('EVM' in chainId) return 'evm';
  if ('Hedera' in chainId) return 'hedera';
  throw new Error(`Unknown ChainId variant: ${JSON.stringify(chainId)}`);
}

// ── Canonical string (mirrors Rust to_canonical_string / from_canonical_str) ─

export function toCanonicalString(chainId: AppChainId): string {
  const evm = evmChainNumber(chainId);
  if (evm != null) return `evm:${evm}`;

  const hedera = hederaNetworkId(chainId);
  if (hedera != null) return `hedera:${hedera}`;

  throw new Error(`Unsupported ChainId variant: ${JSON.stringify(chainId)}`);
}

export function fromCanonicalString(s: string): AppChainId | null {
  const idx = s.indexOf(':');
  if (idx === -1) return null;
  const family = s.slice(0, idx).toLowerCase();
  const id = s.slice(idx + 1);
  switch (family) {
    case 'evm': {
      const n = Number(id);
      return Number.isFinite(n) && n > 0 ? createEvmChainId(n) : null;
    }
    case 'hedera':
      return id.length > 0 ? { Hedera: id } : null;
    default:
      return null;
  }
}

// ── Equality ────────────────────────────────────────────────────────────────

export function chainIdsEqual(a: AppChainId, b: AppChainId): boolean {
  return toCanonicalString(a) === toCanonicalString(b);
}

// ── Metadata accessors ──────────────────────────────────────────────────────

export function chainMeta(chainId: AppChainId): ChainMeta | undefined {
  return registryByKey.get(toCanonicalString(chainId));
}

export function chainLabel(chainId: AppChainId): string {
  return chainMeta(chainId)?.label ?? toCanonicalString(chainId);
}

export function explorerAddressUrl(
  address: string,
  chainId: AppChainId | undefined | null,
): string | null {
  if (!chainId) return null;
  const meta = chainMeta(chainId);
  return meta ? `${meta.explorerAddressUrl}${address}` : null;
}

/**
 * Build a block-explorer URL for a given block number. Derives the base URL
 * from the chain's registered `explorerAddressUrl` by swapping the `/address/`
 * path segment for `/block/` — every Etherscan-family explorer we register
 * (Etherscan, Arbiscan, Polygonscan, Optimism, Base) follows this pattern.
 *
 * Returns `null` for chains without a registered explorer or when the base URL
 * can't be transformed (e.g. Hedera uses `/account/`, not `/address/`).
 */
export function explorerBlockUrl(
  blockNumber: number | bigint,
  chainId: AppChainId | undefined | null,
): string | null {
  if (!chainId) return null;
  const meta = chainMeta(chainId);
  if (!meta || meta.type !== 'evm') return null;
  // `/address/` → `/block/<n>`. The trailing slash on explorerAddressUrl is
  // required (it's how the base URL is stored), so the regex anchors on it.
  const base = meta.explorerAddressUrl.replace(/\/address\/$/, '/block/');
  if (base === meta.explorerAddressUrl) return null; // unexpected shape
  return `${base}${blockNumber}`;
}

export function formatNetworkLabel(network: string): string {
  const parsed = fromCanonicalString(network);
  return parsed ? chainLabel(parsed) : network;
}

// ── Address encoding ────────────────────────────────────────────────────────

export function hexToChainAddress(hex: string): { EVM: number[] } {
  const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
  if (clean.length !== 40 || !/^[0-9a-fA-F]+$/.test(clean)) {
    throw new Error(`Invalid EVM address: expected 40 hex characters, got "${hex}"`);
  }
  const bytes: number[] = [];
  for (let i = 0; i < clean.length; i += 2) {
    bytes.push(parseInt(clean.substring(i, i + 2), 16));
  }
  return { EVM: bytes };
}
